import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import Editor, { OnMount } from '@monaco-editor/react';
import { WizardItemType } from '../../Enums/WizardItemType.enum';
import { WizardItemSubType } from '../../Enums/WizardItemSubtype.enum';
import { WizardItemRuleType } from '../../Enums/WizardItemRuleType.enum';
import { WizardActionType } from '../../Enums/WizardActionType.enum';
import {
	addWizardDatePicker,
	addWizardRadioList,
	addWizardSelect,
	addWizardSubmitButton,
	addWizardTextField,
} from '../../Services/wizardBuilder.service';
import styles from './WizardBuilder.module.scss';
import WizardRender from '../WizardRender/WizardRender';
import Input from '../../../BaseModule/Components/Form/Components/Input';
import useWizard from '../../Hooks/useWizard';
import { normalizeWizardFormValues } from '../../Helpers/WizardValues.helper';

// @ts-ignore
type MonacoEditor = import('monaco-editor').editor.IStandaloneCodeEditor;

const DEFAULT_JSON = `{
  "key": "stepKey",
  "rank": 1,
  "settings": null,
  "type": "dynamic",
  "subtype": null,
  "children": [],
  "parentId": null
}
`;

const LS_KEY = 'wizardBuilder:lastJson:v1';

function enumValues<E extends Record<string, string | number>>(
	e: E,
): Array<E[keyof E]> {
	return Object.values(e).filter(v => typeof v === 'string') as Array<
		E[keyof E]
	>;
}

const WizardBuilder: React.FC = () => {
	const [wizardKey, setWizardKey] = useState<string | undefined>(undefined);
	const { activeStep, submitStep, isLoading } = useWizard({
		key: wizardKey,
		initialData: {},
		onWizardDone: () => {},
	});

	const [wizardKeyInput, setWizardKeyInput] = useState<string>('');
	const [jsonText, setJsonText] = useState(() => {
		try {
			const saved = localStorage.getItem(LS_KEY);
			return saved && saved.trim().length ? saved : DEFAULT_JSON;
		} catch {
			return DEFAULT_JSON;
		}
	});

	const [parseError, setParseError] = useState<string | null>(null);
	const [isAddOpen, setIsAddOpen] = useState(false);

	const editorRef = useRef<MonacoEditor | null>(null);
	const addWrapRef = useRef<HTMLDivElement | null>(null);

	const parsed = useMemo(() => {
		try {
			const obj = JSON.parse(jsonText);
			setParseError(null);
			return obj;
		} catch (e) {
			setParseError(e instanceof Error ? e.message : 'Invalid JSON');
			return null;
		}
	}, [jsonText]);

	useEffect(() => {
		const t = window.setTimeout(() => {
			try {
				localStorage.setItem(LS_KEY, jsonText);
			} catch (e) {
				console.error('Failed to save JSON to localStorage:', e);
			}
		}, 250);

		return () => window.clearTimeout(t);
	}, [jsonText]);

	useEffect(() => {
		if (!activeStep) return;
		try {
			setJsonText(JSON.stringify(activeStep, null, 2));
		} catch (e) {
			console.error('Failed to stringify active step:', e);
		}
	}, [activeStep]);

	useEffect(() => {
		if (!isAddOpen) return;

		const onMouseDown = (e: MouseEvent) => {
			const el = addWrapRef.current;
			if (!el) return;
			if (e.target instanceof Node && !el.contains(e.target))
				setIsAddOpen(false);
		};

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setIsAddOpen(false);
		};

		document.addEventListener('mousedown', onMouseDown);
		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('mousedown', onMouseDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [isAddOpen]);

	const insertJsonAtCursor = useCallback((obj: unknown) => {
		const editor = editorRef.current;
		if (!editor) return;

		const selection = editor.getSelection();
		if (!selection) return;

		const text = JSON.stringify(obj, null, 2) + ',\n';

		editor.executeEdits('wizard-builder-insert', [
			{ range: selection, text, forceMoveMarkers: true },
		]);

		editor.getAction('editor.action.formatDocument')?.run();
		editor.focus();
	}, []);

	const onMount: OnMount = (editor, monaco) => {
		editorRef.current = editor;

		const itemTypes = enumValues(WizardItemType);
		const subTypes = enumValues(WizardItemSubType);
		const ruleTypes = enumValues(WizardItemRuleType);
		const actionTypes = enumValues(WizardActionType);

		monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
			validate: true,
			allowComments: false,
			enableSchemaRequest: false,
			schemas: [
				{
					uri: 'inmemory://wizard.schema.json',
					fileMatch: ['*'],
					schema: {
						type: 'object',
						properties: {
							type: { type: 'string' },
							subtype: {
								anyOf: [{ enum: subTypes }, { type: 'null' }],
							},
							children: {
								type: 'array',
								items: {
									type: 'object',
									properties: {
										type: { enum: itemTypes },
										subtype: {
											anyOf: [{ enum: subTypes }, { type: 'null' }],
										},
										rules: {
											type: 'array',
											items: {
												type: 'object',
												properties: {
													type: { enum: ruleTypes },
													value: {},
													message: { type: 'string' },
												},
												required: ['type'],
											},
										},
										action: {
											type: 'object',
											properties: {
												type: { enum: actionTypes },
											},
										},
									},
									additionalProperties: true,
								},
							},
						},
						additionalProperties: true,
					},
				},
			],
		});

		editor.updateOptions({
			minimap: { enabled: false },
			scrollBeyondLastLine: false,
			fontSize: 13,
			tabSize: 2,
			formatOnPaste: true,
			formatOnType: true,
			wordWrap: 'on',
		});
	};

	const handleLoad = useCallback(() => {
		setWizardKey(wizardKeyInput.trim());
	}, [wizardKeyInput]);

	const handleSubmit = async (values: object) => {
		const normalized = normalizeWizardFormValues(values);
		await submitStep(normalized);
	};

	const handleAdd = useCallback(
		(kind: WizardItemType) => {
			setIsAddOpen(false);
			switch (kind) {
				case WizardItemType.TEXT_FIELD:
					return insertJsonAtCursor(addWizardTextField());
				case WizardItemType.SELECT:
					return insertJsonAtCursor(addWizardSelect());
				case WizardItemType.RADIO_LIST:
					return insertJsonAtCursor(addWizardRadioList());
				case WizardItemType.DATE:
					return insertJsonAtCursor(addWizardDatePicker());
				case WizardItemType.SUBMIT_BUTTON:
					return insertJsonAtCursor(addWizardSubmitButton());
			}
		},
		[insertJsonAtCursor],
	);

	return (
		<div className={styles.wrapper}>
			<div className={styles.left}>
				<div className={styles.header}>
					<div className={styles.actions}>
						<div className={styles.wizardKey}>
							<Input
								name='wizardKey'
								value={wizardKeyInput}
								onChange={(e: any) => setWizardKeyInput(e?.target?.value ?? '')}
							/>
							<button
								className={styles.button}
								type='button'
								onClick={handleLoad}
							>
								Načíst wizard
							</button>
						</div>

						<div ref={addWrapRef} className={styles.addWrap}>
							<button
								type='button'
								onClick={() => setIsAddOpen(v => !v)}
								aria-haspopup='menu'
								aria-expanded={isAddOpen}
								className={styles.button}
							>
								Přidat položku
							</button>

							{isAddOpen && (
								<div className={styles.addMenu} role='menu'>
									<button
										type='button'
										role='menuitem'
										onClick={() => handleAdd(WizardItemType.TEXT_FIELD)}
									>
										Textové pole
									</button>
									<button
										type='button'
										role='menuitem'
										onClick={() => handleAdd(WizardItemType.SELECT)}
									>
										Select
									</button>
									<button
										type='button'
										role='menuitem'
										onClick={() => handleAdd(WizardItemType.RADIO_LIST)}
									>
										Radiolist
									</button>
									<button
										type='button'
										role='menuitem'
										onClick={() => handleAdd(WizardItemType.DATE)}
									>
										Datepicker
									</button>
									<button
										type='button'
										role='menuitem'
										onClick={() => handleAdd(WizardItemType.SUBMIT_BUTTON)}
									>
										Submit button
									</button>
								</div>
							)}
						</div>
					</div>
				</div>

				<div className={styles.editor}>
					<Editor
						height='100%'
						defaultLanguage='json'
						value={jsonText}
						onChange={v => setJsonText(v ?? '')}
						onMount={onMount}
						theme='vs-dark'
					/>
				</div>

				<div className={styles.statusBar}>
					{parseError ? (
						<span>JSON není validní: {parseError}</span>
					) : (
						<span>JSON validní ✅</span>
					)}
				</div>
			</div>

			<div className={styles.right}>
				<div className={styles.previewBody}>
					{parsed ? (
						<WizardRender activeStep={parsed} onSubmit={handleSubmit} />
					) : (
						<div className={styles.previewInvalid}>
							Nejde vykreslit — JSON není validní.
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default WizardBuilder;
